import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi, type LocationNode } from '../../lib/adminApi';

/**
 * Corrects a place without destroying it.
 *
 * The reason this exists: `FOOL 1` was a misspelt floor with sixteen rooms under it.
 * Delete refused (rightly — the children block it), retiring it would have hidden a
 * floor in daily use, and recreating it would have re-parented every room onto a new id.
 * None of those is "fix the spelling".
 */
export function EditPlaceDialog({
  node,
  onSaved,
  onCancel,
}: {
  node: LocationNode;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(node.name);
  const [floor, setFloor] = useState(node.floor ?? '');
  const [qrCode, setQrCode] = useState(node.qrCode ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changed =
    name.trim() !== node.name ||
    floor.trim() !== (node.floor ?? '') ||
    qrCode.trim() !== (node.qrCode ?? '');
  const ready = name.trim().length > 0 && changed && !busy;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready) return;

    setBusy(true);
    setError(null);
    try {
      await adminApi.superAdmin.locations.edit(node.id, {
        name: name.trim(),
        // Empty means cleared, not "leave alone" — the field was visibly blanked.
        floor: floor.trim() || null,
        qrCode: qrCode.trim() || null,
      });
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('superAdmin.failed'));
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-3 sm:items-center">
      <form
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-label={t('superAdmin.rooms.editHeading', { subject: node.name })}
        className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-950 p-4"
      >
        <p className="text-sm font-semibold text-slate-100">
          {t('superAdmin.rooms.editHeading', { subject: node.name })}
        </p>

        {(
          [
            ['editName', name, setName, 120],
            ['editFloor', floor, setFloor, 40],
            ['editQr', qrCode, setQrCode, 80],
          ] as const
        ).map(([key, value, set, max]) => (
          <label key={key} className="mt-3 block text-xs text-slate-400">
            {t(`superAdmin.rooms.${key}`)}
            <input
              value={value}
              onChange={(event) => set(event.target.value)}
              maxLength={max}
              autoCapitalize="off"
              autoCorrect="off"
              className="mt-1 min-h-[48px] w-full rounded border border-slate-700 bg-slate-900 px-3 text-sm text-slate-100"
            />
          </label>
        ))}

        {error && (
          <p role="alert" className="mt-2 text-sm text-red-400">
            {error}
          </p>
        )}
        {!changed && (
          <p className="mt-2 text-xs text-slate-500">{t('superAdmin.rooms.editUnchanged')}</p>
        )}

        <div className="mt-4 flex gap-2">
          <button
            type="submit"
            disabled={!ready}
            className="min-h-[48px] flex-1 rounded-lg bg-brookfields-green ring-1 ring-brookfields-gold/70 text-sm font-semibold text-white disabled:bg-brookfields-green-dark disabled:text-slate-400 disabled:ring-brookfields-gold-dark"
          >
            {busy ? t('superAdmin.rooms.editSaving') : t('superAdmin.rooms.editSave')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="min-h-[48px] rounded-lg bg-transparent px-4 text-sm text-slate-400"
          >
            {t('superAdmin.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}
